import type { Order, TrackingEvent } from './types';
import { DELIVERED_STATUS } from '../../../constants/order';
import { formatLongDate, formatDateTime } from '../../../utils/date';

/**
 * Transforms Deleo trace events to UI TrackingEvent format
 * API returns timestamps in KST (Korea Standard Time, UTC+9)
 * Converts to user's local timezone for display
 */
export const transformTrackingEvents = (order: Order): TrackingEvent[] => {
  const traceList = order.deleoData?.deliveryTrace?.traceInfoList?.[0]?.traceList;

  if (!traceList || traceList.length === 0) {
    return [];
  }

  return traceList.map((event, index) => {
    const isFirstEvent = index === 0;
    const isDelivered = event.status === DELIVERED_STATUS;
    const isCurrentEvent = isFirstEvent && !isDelivered;

    const eventStatus: TrackingEvent['status'] = isCurrentEvent ? 'current' : 'completed';

    // API format: "2025-12-18 09:08:00" in KST
    // Add KST timezone offset (+09:00) to ensure correct interpretation
    const eventDate = new Date(event.eventTime.replace(' ', 'T') + '+09:00');
    const formattedTimestamp = formatDateTime(eventDate);

    return {
      id: `${index}-${event.eventTime}`,
      type: event.status,
      title: event.description,
      timestamp: formattedTimestamp,
      status: eventStatus,
    };
  });
};

/**
 * Gets the tracking number from the order
 */
export const getTrackingNumber = (order: Order): string => {
  return order.deleoData?.deleoTrackingNo ?? '';
};

/**
 * Gets the delivery date from the order tracking events
 * API returns timestamps in KST (Korea Standard Time, UTC+9)
 * Converts to user's local timezone for display
 */
export const getDeliveryDate = (order: Order): string | undefined => {
  const traceList = order.deleoData?.deliveryTrace?.traceInfoList?.[0]?.traceList;
  const deliveredEvent = traceList?.find((event) => event.status === DELIVERED_STATUS);

  if (deliveredEvent?.eventTime) {
    // API format: "2025-12-18 09:08:00" in KST
    // Add KST timezone offset (+09:00) to ensure correct interpretation
    const date = new Date(deliveredEvent.eventTime.replace(' ', 'T') + '+09:00');
    return formatLongDate(date);
  }

  return undefined;
};

/**
 * Gets the order date formatted
 * orderDateTime is in GMT without timezone suffix, so we add 'Z' to indicate UTC
 */
export const getFormattedOrderDate = (order: Order): string => {
  // API format: "2025-12-11T13:08:19" in GMT (UTC+0)
  // Add 'Z' suffix to ensure correct interpretation as UTC
  const date = new Date(`${order.orderDateTime}Z`);
  return formatLongDate(date);
};

/**
 * Calculates the item price from line item data
 * Uses direct price if available, otherwise calculates from total and quantity
 */
const calculateItemPrice = (item: Order['lineItems'][0]): number => {
  if (item.price !== undefined) {
    return item.price;
  }

  if (item.total !== undefined && item.quantity > 0) {
    return item.total / item.quantity;
  }

  return 0;
};

/**
 * Transforms Order API response to display format for OrderCard
 */
export const transformOrderForDisplay = (order: Order) => {
  return {
    id: Number(order.orderId),
    orderNumber: `#${order.orderId}`,
    status: order.status,
    orderDate: getFormattedOrderDate(order),
    deliveryDate: getDeliveryDate(order) ?? 'Pending',
    trackingNumber: getTrackingNumber(order),
    total: order.totalAmount,
    currency: order.currency,
    items: order.lineItems.map((item, index) => ({
      id: index,
      name: item.name,
      imageUrl: item.wooCommerceImageUrl,
      variant: [item.productSize, item.productPack].filter(Boolean).join(' '),
      quantity: item.quantity,
      price: calculateItemPrice(item),
    })),
    trackingEvents: transformTrackingEvents(order),
  };
};

